import React, { cloneElement, useId } from "react";
import useAutosave from "./useAutosave";
import Icon from "./Icon";
import "./Field.scss";

// Fontaine M26 — Field: label + control + hint + error + Saving… → Saved ✓ status. The auto-save composite:
// every edit persists the moment it is made (debounced); the field change IS the commit. No Save buttons.
// Works with any control (Input, Select, DatePicker, NumberInput, Textarea, Toggle…): the control is the
// single child and receives id / aria-describedby / aria-invalid / invalid.

const STATUS_TEXT = { idle: "", saving: "Saving…", saved: "Saved", error: "Couldn't save" };

/**
 * @param {ReactNode} label
 * @param {ReactElement} children              ONE control
 * @param {ReactNode} [hint]
 * @param {string} [error]                     validation error (shown instead of the hint)
 * @param {boolean} [required]
 * @param {"stacked"|"inline"} [layout="stacked"]   inline = label left on laptop (still stacked on phone)
 * @param {(value) => Promise<any>} [onSave]   enables auto-save: the control's onChange value is persisted
 * @param {(event|value) => any} [getValue]    how to read the value from the control's onChange (default: event.target.value or the value itself)
 * @param {number} [debounce=600]
 * @param {"idle"|"saving"|"saved"|"error"} [status]   external status (when the parent owns the save)
 * @param {string} [statusError]
 */
export default function Field({ label, children, hint, error, required = false, layout = "stacked", onSave, getValue, debounce = 600, status: statusProp, statusError, onRetry, className = "", id, ...rest }) {
  const autoId = useId();
  const fieldId = id || `ft-field-${autoId.replace(/:/g, "")}`;
  const auto = useAutosave(onSave || (async () => {}), { debounce });
  const status = onSave ? auto.status : statusProp || "idle";
  const saveError = onSave ? auto.error : statusError;
  const showError = error || saveError;

  const child = React.Children.only(children);
  const read = (arg) => (getValue ? getValue(arg) : arg && arg.target && "value" in arg.target ? arg.target.value : arg);
  const control = cloneElement(child, {
    id: child.props.id || fieldId,
    "aria-describedby": [hint || showError ? `${fieldId}-desc` : null, child.props["aria-describedby"]].filter(Boolean).join(" ") || undefined,
    "aria-invalid": showError ? true : child.props["aria-invalid"],
    invalid: showError ? true : child.props.invalid,
    "aria-required": required || undefined,
    onChange: (...args) => {
      child.props.onChange?.(...args);
      if (onSave) auto.commit(read(args[0]));
    },
    onBlur: (...args) => { child.props.onBlur?.(...args); if (onSave) auto.flush(); },
  });

  const classes = ["ft-field", `ft-field--${layout === "inline" ? "inline" : "stacked"}`, showError ? "is-error" : "", `is-${status}`, className].filter(Boolean).join(" ");

  return (
    <div className={classes} {...rest}>
      <div className="ft-field__head">
        <label className="ft-field__label" htmlFor={child.props.id || fieldId}>
          {label}
          {required ? <span className="ft-field__req" aria-hidden="true"> *</span> : null}
        </label>
        {status !== "idle" ? (
          <span className={`ft-field__status is-${status}`} role="status" aria-live="polite">
            <span className="ft-field__status-dot" aria-hidden="true" />
            {status === "saved" ? <Icon name="Check" size="xs" /> : null}
            {STATUS_TEXT[status]}
            {status === "error" ? (
              <button type="button" className="ft-field__retry" onClick={onSave ? auto.retry : onRetry}>Retry</button>
            ) : null}
          </span>
        ) : null}
      </div>
      <div className="ft-field__control">{control}</div>
      {hint || showError ? (
        <div className={`ft-field__desc${showError ? " is-error" : ""}`} id={`${fieldId}-desc`} role={showError ? "alert" : undefined}>
          {showError || hint}
        </div>
      ) : null}
    </div>
  );
}
